import { Box, Flex } from "rebass/styled-components";
import styled, { keyframes } from "styled-components";

// ── Modal shell ──────────────────────────────────────────────────────────────

export const Overlay = styled(Box)`
  align-items: center;
  background: rgba(0, 0, 0, 0.8);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1100;
`;

export const Container = styled(Flex)`
  background: ${({ theme }) => theme.colors.darkerNed};
  border-radius: 12px;
  flex-direction: column;
  max-height: 88vh;
  max-width: 560px;
  overflow: hidden;
  width: 92%;
`;

export const Header = styled(Flex)`
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  justify-content: space-between;
  padding: 16px 20px;
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  display: flex;
  padding: 4px;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }
`;

export const Body = styled(Flex)`
  flex-direction: column;
  gap: 18px;
  overflow-y: auto;
  padding: 20px;
`;

// Back sits bottom-left; primary actions group bottom-right
export const Footer = styled(Flex)`
  align-items: center;
  border-top: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  gap: 12px;
  justify-content: space-between;
  padding: 16px 20px;
`;

export const FooterActions = styled(Flex)`
  align-items: center;
  gap: 12px;
`;

// ── Upload step ──────────────────────────────────────────────────────────────

export const HintText = styled.div`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 12px;
`;

export const HiddenInput = styled.input`
  display: none;
`;

export const StatusText = styled.div`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 14px;
  text-align: center;
`;

export const ErrorText = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: 13px;
  text-align: center;
`;

export const ProgressTrack = styled(Box)`
  background: ${({ theme }) => theme.colors.grey825};
  border-radius: 4px;
  height: 6px;
  overflow: hidden;
  width: 100%;
`;

export const ProgressFill = styled(Box)<{ $pct: number }>`
  background: ${({ theme }) => theme.colors.envy};
  height: 100%;
  transition: width 0.2s ease;
  width: ${({ $pct }) => `${$pct}%`};
`;

export const CenteredColumn = styled(Flex)`
  align-items: center;
  flex-direction: column;
  gap: 14px;
  padding: 32px 8px;
`;

// ── Beat visualizer ──────────────────────────────────────────────────────────

// wave launches from the core's expanded edge (peak bar scale ≈ 1.3) and races
// outward, decelerating (ease-out) and fading — clipped by PulseStage
// --ring-base is the core's current base scale (grows 0.5 → 1 over the
// build-up), so the wave starts at the core's edge whatever its size
const beatFlash = keyframes`
  0% { transform: scale(calc(var(--ring-base, 1) * 1.3)); opacity: 0.6; }
  100% { transform: scale(calc(var(--ring-base, 1) * 2.05)); opacity: 0; }
`;

export const VizStage = styled(Flex)`
  align-items: center;
  background: ${({ theme }) => theme.colors.grey825};
  border-radius: 10px;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  padding: 10px 16px 12px;
  position: relative;
`;

// contains the expanding waves; the mask fades them out near the top/bottom
// edges so they dissolve instead of being cut off by the overflow clip
export const PulseStage = styled(Flex)`
  align-items: center;
  height: 72px;
  justify-content: center;
  overflow: hidden;
  position: relative;
  width: 100%;
  -webkit-mask-image: linear-gradient(
    180deg,
    transparent 0,
    black 12px,
    black calc(100% - 12px),
    transparent 100%
  );
  mask-image: linear-gradient(
    180deg,
    transparent 0,
    black 12px,
    black calc(100% - 12px),
    transparent 100%
  );
`;

export const PulseWrap = styled(Box)`
  height: 44px;
  position: relative;
  width: 44px;
`;

// the filled core — its background colour + scale are set inline per frame
// (colour reflects position in the song; scale bounces on the beat)
export const PulseCore = styled(Box)`
  border-radius: 50%;
  inset: 0;
  position: absolute;
  transform-origin: center;
  will-change: transform;
`;

// keyed element: remounts per bar so the one-shot ring animation replays;
// its colour (set inline) is the core's colour at the moment of emit
export const FlashRing = styled(Box)`
  animation: ${beatFlash} 0.45s ease-out forwards;
  border: 2px solid ${({ theme }) => theme.colors.ice};
  border-radius: 50%;
  inset: 0;
  pointer-events: none;
  position: absolute;
`;

export const BpmBadge = styled.div`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 12px;
  letter-spacing: 0.04em;
`;

export const BpmValue = styled.span`
  color: ${({ theme }) => theme.colors.ice};
  font-weight: 700;
`;

// legend
export const Legend = styled(Flex)`
  flex-wrap: wrap;
  gap: 4px 14px;
  justify-content: center;
`;

export const LegendItem = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 11px;
  gap: 5px;
`;

export const LegendSwatch = styled.span<{ $variant: string }>`
  border-radius: 2px;
  display: inline-block;
  height: 10px;
  width: 10px;
  background: ${({ $variant, theme }) =>
    $variant === "drop"
      ? theme.colors.envy
      : $variant === "candidate"
      ? theme.colors.caution
      : $variant === "beat"
      ? theme.colors.ice
      : $variant === "lead"
      ? theme.colors.envyAlpha40
      : theme.colors.error};
  opacity: ${({ $variant }) => ($variant === "beat" ? 0.5 : 1)};
`;

// a labelled scrub bar (overview or zoomed)
export const ScrubBlock = styled(Flex)`
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

export const ScrubHead = styled(Flex)`
  align-items: center;
  justify-content: space-between;
`;

export const ScrubLabel = styled.div`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 12px;
  font-weight: 600;
`;

export const ScrubBar = styled(Box)<{ $tall?: boolean; $picking?: boolean }>`
  background: ${({ theme }) => theme.colors.darkerNed};
  border: 1px solid
    ${({ $picking, theme }) =>
      $picking ? theme.colors.envy : theme.colors.chalkAlpha20};
  border-radius: 6px;
  cursor: ${({ $picking }) => ($picking ? "crosshair" : "pointer")};
  height: ${({ $tall }) => ($tall ? "56px" : "40px")};
  overflow: hidden;
  position: relative;
  width: 100%;
`;

// The zoomed view's full-song-width inner track. The RAF loop scrolls it with
// translateX so the playhead stays centred — no per-frame React work. Bar
// ticks are a repeating background gradient (one tile per bar, set inline),
// not DOM nodes, so a long song costs nothing extra.
export const ZoomTrack = styled.div`
  background-repeat: repeat-x;
  bottom: 0;
  left: 0;
  position: absolute;
  top: 0;
  will-change: transform;
`;

// arms "click a bar to make it the drop" mode on the scrub bars
export const PickDropButton = styled.button<{ $active?: boolean }>`
  align-items: center;
  align-self: flex-start;
  background: none;
  border: 1px solid
    ${({ $active, theme }) =>
      $active ? theme.colors.envy : theme.colors.chalkAlpha20};
  border-radius: 12px;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.envy : theme.colors.chalk};
  cursor: pointer;
  display: flex;
  font-size: 11px;
  gap: 5px;
  padding: 4px 10px;

  &:hover {
    border-color: ${({ theme }) => theme.colors.envy};
    color: ${({ theme }) => theme.colors.envy};
  }
`;

// magnifier badge marking the zoomed timeline — sits just above its top-left
export const ZoomBadge = styled.div`
  align-self: flex-start;
  color: ${({ theme }) => theme.colors.chalk};
  line-height: 1;
  margin-bottom: -2px;
  opacity: 0.65;
`;

export const ScrubCenterLine = styled(Box)`
  background: ${({ theme }) => theme.colors.chalkAlpha20};
  height: 2px;
  left: 0;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
`;

// shaded build-up region before the drop (zoomed bar)
export const LeadBand = styled(Box)<{ $left: number; $width: number }>`
  background: ${({ theme }) => theme.colors.envyAlpha40};
  bottom: 0;
  left: ${({ $left }) => `${$left}%`};
  opacity: 0.25;
  position: absolute;
  top: 0;
  width: ${({ $width }) => `${$width}%`};
`;

// clickable drop candidate (both bars) — gold tick; chosen one is taller
export const DropMarker = styled.button<{ $left: number; $chosen?: boolean }>`
  background: ${({ theme }) => theme.colors.caution};
  border: none;
  border-radius: 2px;
  cursor: pointer;
  height: ${({ $chosen }) => ($chosen ? "30px" : "18px")};
  left: ${({ $left }) => `${$left}%`};
  opacity: ${({ $chosen }) => ($chosen ? 1 : 0.6)};
  padding: 0;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  transition: transform 0.1s ease;
  width: 3px;
  z-index: 2;

  &:hover {
    opacity: 1;
    transform: translate(-50%, -50%) scaleX(1.8);
  }
`;

// where the drop actually lands = chosen candidate + beat-alignment offset;
// slides off the gold candidate as alignment is nudged (green, not clickable)
export const AlignedDrop = styled(Box)<{ $left: number }>`
  background: ${({ theme }) => theme.colors.envy};
  border-radius: 2px;
  bottom: 2px;
  box-shadow: 0 0 6px ${({ theme }) => theme.colors.envy};
  left: ${({ $left }) => `${$left}%`};
  pointer-events: none;
  position: absolute;
  top: 2px;
  transform: translateX(-50%);
  width: 3px;
  z-index: 4;
`;

// position (left / display) is driven imperatively via a ref each frame, so the
// playhead tracks at the display's native refresh rate without re-rendering
export const PlayLine = styled.div`
  background: ${({ theme }) => theme.colors.error};
  bottom: 0;
  left: 0;
  pointer-events: none;
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  width: 2px;
  z-index: 3;
`;

export const TransportRow = styled(Flex)`
  align-items: center;
  gap: 12px;
  width: 100%;
`;

export const PlayButton = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.colors.envy};
  border: none;
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.darkerNed};
  cursor: pointer;
  display: flex;
  height: 36px;
  justify-content: center;
  width: 36px;

  &:hover {
    filter: brightness(1.1);
  }
`;

export const RestartButton = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.colors.grey825};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  display: flex;
  height: 32px;
  justify-content: center;
  width: 32px;

  &:hover {
    border-color: ${({ theme }) => theme.colors.envy};
    color: ${({ theme }) => theme.colors.envy};
  }
`;

// right-aligned in the transport row; bigger than the other transport buttons
export const MetronomeButton = styled(RestartButton)<{ $active?: boolean }>`
  border-color: ${({ $active, theme }) =>
    $active ? theme.colors.envy : theme.colors.chalkAlpha20};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.envy : theme.colors.ice};
  height: 40px;
  margin-left: auto;
  width: 40px;
`;

export const VolumeButton = styled(RestartButton)`
  height: 40px;
  width: 40px;
`;

// hover/focus-opened vertical mix slider (metronome at the top, song at the
// bottom). The popup touches the button (no gap) so the pointer can travel
// into it without the hover breaking; focus-within keeps it open while dragging.
export const VolumeWrap = styled.div`
  position: relative;

  &:hover > div,
  &:focus-within > div {
    display: flex;
  }
`;

export const VolumePopup = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.colors.darkerNed};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 8px;
  bottom: 100%;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.45);
  color: ${({ theme }) => theme.colors.chalk};
  display: none;
  flex-direction: column;
  gap: 4px;
  left: 50%;
  padding: 8px 6px;
  position: absolute;
  transform: translateX(-50%);
  z-index: 10;
`;

export const VolumeSliderBox = styled.div`
  align-items: center;
  display: flex;
  height: 90px;
  justify-content: center;
  width: 24px;
`;

// a horizontal range rotated upright: value grows upward (toward the metronome)
export const VolumeSlider = styled.input`
  accent-color: ${({ theme }) => theme.colors.envy};
  transform: rotate(-90deg);
  width: 90px;
`;

// hover/focus "?" bubble explaining playback latency vs the metronome
export const InfoTip = styled.div`
  position: relative;

  &:hover > div,
  &:focus-within > div {
    display: block;
  }
`;

export const InfoTipIcon = styled.button`
  align-items: center;
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: help;
  display: flex;
  font-size: 11px;
  font-weight: 700;
  height: 18px;
  justify-content: center;
  width: 18px;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }
`;

// $align: which edge hugs the icon — "right" (default) opens leftward, for
// icons near the modal's right edge; "left" opens rightward
export const InfoTipBubble = styled.div<{ $align?: "left" | "right" }>`
  background: ${({ theme }) => theme.colors.darkerNed};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 8px;
  bottom: calc(100% + 8px);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.45);
  color: ${({ theme }) => theme.colors.chalk};
  display: none;
  font-size: 11px;
  line-height: 1.5;
  padding: 10px 12px;
  position: absolute;
  ${({ $align }) => ($align === "left" ? "left: -10px;" : "right: -10px;")}
  width: 260px;
  z-index: 10;

  b {
    color: ${({ theme }) => theme.colors.ice};
  }
`;

export const TimeText = styled.div`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 12px;
  font-variant-numeric: tabular-nums;
`;

// ── Tuning controls ──────────────────────────────────────────────────────────

export const ControlGroup = styled(Flex)`
  flex-direction: column;
  gap: 8px;
`;

export const ControlLabelRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
`;

export const ControlLabel = styled.div`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 13px;
  font-weight: 600;
`;

export const ChipRow = styled(Flex)`
  flex-wrap: wrap;
  gap: 8px;
`;

export const Chip = styled.button<{ $selected?: boolean }>`
  background: ${({ $selected, theme }) =>
    $selected ? theme.colors.envyAlpha40 : theme.colors.grey825};
  border: 1px solid
    ${({ $selected, theme }) =>
      $selected ? theme.colors.envy : theme.colors.chalkAlpha20};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  font-size: 12px;
  padding: 6px 12px;

  &:hover {
    border-color: ${({ theme }) => theme.colors.envy};
  }
`;

export const Stepper = styled(Flex)`
  align-items: center;
  gap: 10px;
`;

export const StepButton = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.colors.grey825};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  display: flex;
  font-size: 16px;
  height: 30px;
  justify-content: center;
  width: 34px;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.envy};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

export const StepValue = styled.div`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  min-width: 96px;
  text-align: center;
`;

export const PhaseEditRow = styled(Flex)`
  align-items: center;
  gap: 6px;
`;

export const PhaseInput = styled.input`
  background: ${({ theme }) => theme.colors.darkerNed};
  border: 1px solid ${({ theme }) => theme.colors.envyAlpha40};
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.ice};
  font-size: 13px;
  outline: none;
  padding: 4px 8px;
  width: 110px;
`;

export const ResetLink = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  font-size: 12px;
  padding: 2px 0;
  text-decoration: underline;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }
`;

// ── Library picker ───────────────────────────────────────────────────────────

export const SectionLabel = styled.div`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

export const CompactDrop = styled(Flex)<{ $active?: boolean }>`
  align-items: center;
  background: ${({ theme }) => theme.colors.grey825};
  border: 1.5px dashed
    ${({ $active, theme }) =>
      $active ? theme.colors.envy : theme.colors.chalkAlpha20};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  gap: 8px;
  justify-content: center;
  padding: 14px;

  &:hover {
    border-color: ${({ theme }) => theme.colors.envy};
  }
`;

export const LibraryList = styled(Flex)`
  flex-direction: column;
  gap: 6px;
  max-height: 340px;
  overflow-x: hidden;
  overflow-y: auto;
  padding-right: 4px;
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colors.chalkAlpha20} transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.chalkAlpha20};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.chalk};
  }
`;

// a div (not a button) so the per-row rename/delete buttons can nest inside
export const LibraryRow = styled.div<{ $disabled?: boolean }>`
  align-items: center;
  background: ${({ theme }) => theme.colors.grey825};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 8px;
  box-sizing: border-box;
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  display: flex;
  gap: 10px;
  opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};
  padding: 10px 12px;
  text-align: left;
  width: 100%;

  &:hover {
    border-color: ${({ $disabled, theme }) =>
      $disabled ? theme.colors.chalkAlpha20 : theme.colors.envy};
  }
`;

export const LibraryRowMain = styled(Flex)`
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const RowActions = styled(Flex)`
  align-items: center;
  flex-shrink: 0;
  gap: 2px;
`;

export const RowIconButton = styled.button<{ $danger?: boolean }>`
  align-items: center;
  background: none;
  border: none;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  display: flex;
  justify-content: center;
  padding: 4px;

  &:hover {
    color: ${({ $danger, theme }) =>
      $danger ? theme.colors.error : theme.colors.ice};
  }
`;

export const ConfirmDelete = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.error};
  cursor: pointer;
  font-size: 11px;
  font-weight: 700;
  padding: 4px;
  white-space: nowrap;
`;

export const TitleInput = styled.input`
  background: ${({ theme }) => theme.colors.darkerNed};
  border: 1px solid ${({ theme }) => theme.colors.envyAlpha40};
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.ice};
  flex: 1;
  font-size: 13px;
  min-width: 0;
  outline: none;
  padding: 4px 8px;
`;

// tune view: current song's name + rename/delete controls
export const SongTitleRow = styled(Flex)`
  align-items: center;
  gap: 8px;
`;

export const SongTitleText = styled.div`
  color: ${({ theme }) => theme.colors.ice};
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const LibrarySongName = styled.div`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const LibraryMeta = styled.div`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 11px;
`;

export const LibraryStatusTag = styled.span<{ $variant: string }>`
  border-radius: 4px;
  color: ${({ $variant, theme }) =>
    $variant === "error" ? theme.colors.error : theme.colors.caution};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  margin-left: auto;
  text-transform: uppercase;
  white-space: nowrap;
`;

export const EmptyLibrary = styled.div`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 12px;
  padding: 8px 0;
  text-align: center;
`;
